import { useState, useEffect } from 'react';
import './TrophyShowcase.css';

const TrophyShowcase = ({ 
  value = 0, 
  title = "Achievements", 
  isUpdating = false 
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [newTrophyAnimation, setNewTrophyAnimation] = useState(false);

  useEffect(() => {
    if (value > displayValue) {
      // Animate new trophies appearing
      setNewTrophyAnimation(true);
      const timer = setTimeout(() => {
        setDisplayValue(value);
        setNewTrophyAnimation(false);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setDisplayValue(value);
    }
  }, [value, displayValue]);

  // Generate trophy tiers based on value
  const getTrophyTiers = () => {
    const tiers = [];
    let remaining = displayValue;
    
    // Diamond trophies (every 100)
    const diamond = Math.floor(remaining / 100);
    if (diamond > 0) {
      tiers.push({ type: 'diamond', count: diamond, icon: '💎', color: '#e1f5fe' });
      remaining -= diamond * 100;
    }
    
    // Gold trophies (every 10)
    const gold = Math.floor(remaining / 10);
    if (gold > 0) {
      tiers.push({ type: 'gold', count: gold, icon: '🏆', color: '#fff9c4' });
      remaining -= gold * 10;
    }
    
    // Silver trophies (remaining)
    if (remaining > 0) {
      tiers.push({ type: 'silver', count: remaining, icon: '🥈', color: '#f5f5f5' });
    }
    
    return tiers;
  };

  const generateTrophies = () => {
    const tiers = getTrophyTiers();
    const trophies = [];
    let trophyIndex = 0;
    
    tiers.forEach((tier) => {
      for (let i = 0; i < tier.count; i++) {
        const delay = trophyIndex * 0.1;
        trophies.push(
          <div
            key={`${tier.type}-${i}`}
            className={`trophy ${tier.type} ${newTrophyAnimation && trophyIndex >= displayValue - 1 ? 'new' : ''}`}
            style={{
              animationDelay: `${delay}s`,
              '--trophy-color': tier.color
            }}
          >
            <span className="trophy-icon">{tier.icon}</span>
            {tier.type === 'diamond' && (
              <div className="trophy-sparkles">
                <span>✨</span>
                <span>✨</span>
                <span>✨</span>
              </div>
            )}
          </div>
        );
        trophyIndex++;
      }
    });
    
    return trophies;
  };

  const generateParticles = () => {
    if (!newTrophyAnimation) return null;
    
    const particles = [];
    for (let i = 0; i < 15; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            left: `${20 + Math.random() * 60}%`,
            animationDelay: `${Math.random() * 0.5}s`,
            '--random-x': `${(Math.random() - 0.5) * 200}px`
          }}
        >
          {['⭐', '✨', '🌟'][Math.floor(Math.random() * 3)]}
        </div>
      );
    }
    return particles;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <div className={`trophy-showcase ${isUpdating ? 'updating' : ''} ${newTrophyAnimation ? 'celebrating' : ''}`}>
      <div className="showcase-header">
        <div className="showcase-value">
          <span className="number">{formatNumber(displayValue)}</span>
          <div className="showcase-title">{title}</div>
        </div>
      </div>
      
      <div className="trophy-container">
        <div className="trophy-grid">
          {generateTrophies()}
        </div>
        
        {displayValue === 0 && (
          <div className="empty-state">
            <div className="empty-trophy">🏆</div>
            <p>First completion coming soon!</p>
          </div>
        )}
      </div>
      
      {newTrophyAnimation && (
        <div className="celebration-effects">
          {generateParticles()}
          <div className="celebration-burst">
            <div className="burst-ring"></div>
            <div className="burst-ring"></div>
            <div className="burst-ring"></div>
          </div>
        </div>
      )}
      
      <div className="showcase-glow"></div>
    </div>
  );
};

export default TrophyShowcase;
